import { Modal, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import TextWinds from '../../../common/TextWinds';
import { getErrorMessages } from '../../../../api/utils/util-func';
import { useAppDispatch } from '../../../../store/hook';
import { openAlert } from '../../../../store/features/modalSlice';
import {
  AssignmentStatusDetailByClassItem,
  RecitationStatusItem,
} from '../../../../constants/types/lms/recitation/type';
import {
  getAssignmentStatusDetailListByClass,
  updateRecitationToDate,
} from '../../../../api/utils/lms/recitation/api';
import { useParams } from 'react-router-dom';
import HFbutton from '../../../button/HFbutton';

interface RecitationListModalProps {
  open: boolean;
  selectedRecitation: RecitationStatusItem;
  refetch: () => void;
  onClose: () => void;
}

/**
 * LMS >
 */
const RecitationListModal = ({
  open,
  selectedRecitation,
  refetch,
  onClose,
}: RecitationListModalProps) => {
  const { classId } = useParams();

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [detailList, setDetailList] = useState<
    AssignmentStatusDetailByClassItem[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [deadline, setDeadline] = useState<Dayjs | null>(null);

  const fetchAssignmentStatusDetailListByClass = async () => {
    if (!selectedRecitation) return;

    try {
      setIsLoading(true);

      const response = await getAssignmentStatusDetailListByClass({
        classId,
        detailId: selectedRecitation.txId.toString(),
      });

      setDetailList(response ?? []);
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedRecitation) {
      fetchAssignmentStatusDetailListByClass();
    }
  }, [selectedRecitation]);

  const handleSaveDeadline = async (assignment: RecitationStatusItem) => {
    if (!deadline) return;

    try {
      setIsSubmitting(true);

      await updateRecitationToDate({
        classId,
        txId: assignment.txId,
        toDate: deadline.format('YYYY-MM-DD'),
      });

      dispatch(openAlert({ message: '마감일이 수정되었습니다.' }));
      onClose();

      await refetch();
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns: ColumnsType<AssignmentStatusDetailByClassItem> = [
    {
      title: 'No.',
      key: 'index',
      align: 'center',
      width: 56,
      render: (_, __, index) => index + 1,
    },
    {
      title: '학생',
      key: 'student',
      width: 100,
      align: 'center',
      render: (_, recitationStatus) => (
        <div className="min-w-0">
          <TextWinds
            type="content_body2"
            color="gray9"
            className="block truncate font-bold"
          >
            {recitationStatus.student.name ?? '-'}
          </TextWinds>

          <TextWinds type="caption1" color="gray6" className="block truncate">
            {recitationStatus.student.uid ?? '-'}
          </TextWinds>
        </div>
      ),
    },
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      width: 120,
      align: 'center',
      render: () => (
        <TextWinds type="content_body3" color="gray8" className="font-medium">
          {selectedRecitation.level ? `Level ${selectedRecitation.level}` : '-'}
        </TextWinds>
      ),
    },
    {
      title: 'Book',
      dataIndex: 'bookTitle',
      key: 'bookTitle',
      width: 120,
      align: 'center',
      render: () => (
        <TextWinds type="content_body3" color="gray8" className="font-medium">
          {selectedRecitation.bookTitle}
        </TextWinds>
      ),
    },
    {
      title: '마감일',
      key: 'createdAt',
      render: () =>
        dayjs(selectedRecitation.dueDate).format('YYYY-MM-DD HH:mm'),
      align: 'center',
    },
  ];

  return (
    <Modal
      title="마감일 수정"
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      zIndex={9999}
    >
      <div className="flex flex-col gap-3 border-t border-gray2 pt-4">
        <section className="flex flex-col gap-3">
          <TextWinds type="content_body2" className="font-bold">
            함께 배정된 AI인증제 목록
          </TextWinds>

          <Table
            columns={columns}
            dataSource={detailList}
            rowKey={(record) => record.progress.id}
            scroll={{ x: 'max-content' }}
            size="small"
            loading={isLoading}
            locale={{ emptyText: '이관 신청 내역이 없습니다.' }}
            pagination={false}
          />
        </section>

        <section className="flex flex-col gap-3">
          <TextWinds type="content_body2" className="font-bold">
            마감일 설정
          </TextWinds>

          <div className="rounded bg-purple0 p-3">
            <TextWinds type="content_body3" color="purple5">
              현재 목록에 표시된 모든 AI인증제의 마감일이 동일하게 변경됩니다.
            </TextWinds>
          </div>

          <div className="rounded-lg bg-gray1 px-4 py-3">
            <div className="flex items-end gap-3">
              <div className="flex flex-col gap-1">
                <span className="h-4 text-xs text-gray6">현재 마감일</span>

                <span className="flex h-7 items-center text-sm font-medium text-gray8">
                  {dayjs(selectedRecitation.dueDate).format('YYYY-MM-DD')}
                </span>
              </div>

              <span className="mb-1 text-gray5">→</span>

              <div className="flex flex-col gap-1">
                <span className="h-4 text-xs text-gray6">변경할 마감일</span>

                <input
                  type="date"
                  min={dayjs().format('YYYY-MM-DD')}
                  value={
                    deadline
                      ? deadline.format('YYYY-MM-DD')
                      : dayjs(selectedRecitation.dueDate).format('YYYY-MM-DD')
                  }
                  onChange={(event) => {
                    const nextDate = dayjs(event.target.value);

                    if (nextDate.isBefore(dayjs(), 'day')) {
                      dispatch(
                        openAlert({
                          message: '오늘 이전 날짜는 선택할 수 없습니다.',
                        }),
                      );
                      return;
                    }

                    setDeadline(nextDate);
                  }}
                  className="h-7 w-[160px] rounded-md border border-gray3 bg-white px-3 text-sm text-gray9 outline-none focus:border-purple5"
                />
              </div>
            </div>
          </div>
        </section>

        <div className="flex flex-col gap-2 sm:flex-row-reverse"></div>

        <div className="flex justify-end gap-2 border-t border-gray2 pt-4">
          <HFbutton
            type="primary-outlined"
            onClick={onClose}
            disabled={isSubmitting}
          >
            취소
          </HFbutton>
          <HFbutton
            onClick={() => handleSaveDeadline(selectedRecitation)}
            disabled={isSubmitting}
          >
            {isSubmitting ? '수정 중...' : '수정'}
          </HFbutton>
        </div>
      </div>
    </Modal>
  );
};

export default RecitationListModal;
